import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import IndentDetails from "../IndentPage/ItemsPage/IndentDetails";
import ItemsTable from "./POItemsTable";
import { useSelector } from "react-redux";
import IndentChangesTracker from "../IndentChangesTracker";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const POInvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { row: initialRow } = location.state || {};

  const [row, setRow] = useState(initialRow);
  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [error, setError] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const [categories, setCategories] = useState([]);
  const currentUserId = useSelector((state) => state.auth.id);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });
  const [storedChangeLogs, setStoredChangeLogs] = useState([]);

  // Error logging utility
  const logError = useCallback((context, error) => {
    console.error(`[PDIndentPage] ${context}:`, error);
  }, []);

  // Fetch stored change logs
  useEffect(() => {
    const fetchChangeLogs = async () => {
      try {
        if (!row?.IndentID) return;

        const response = await axios.get(
          `${API_BASE_URL}/change_log/change-logspo/${row.IndentID}`
        );
        setStoredChangeLogs(response.data);
      } catch (error) {
        logError("Fetch Change Logs", error);
      }
    };

    const fetchIndentDetails = async () => {
      try {
        if (!row?.IndentID) return;

        const response = await axios.get(`${API_BASE_URL}/popage/indent`, {
          params: { indentId: row.IndentID },
        });
        if (response.data) {
          setRow((prev) => ({
            ...prev,
            ...response.data,
          }));
        }
      } catch (error) {
        logError("Fetch Indent Details", error);
      }
    };

    if (row?.IndentID) {
      // Parallelize both API calls since they're independent
      Promise.all([fetchChangeLogs(), fetchIndentDetails()]);
    }
  }, [row?.IndentID, logError]);

  // Save change log to backend
  const saveChangeLog = useCallback(
    async (changeLogData) => {
      try {
        await axios.post(`${API_BASE_URL}/change_log/change-logs`, {
          indentId: row.IndentID,
          userId: currentUserId,
          currentlevel: "PO",
          ...changeLogData,
        });
      } catch (error) {
        logError("Save Change Log", error);
        setError("Failed to save change log");
      }
    },
    [row?.IndentID, currentUserId]
  );

  // Fetch draft items
  useEffect(() => {
    const fetchDraftItems = async () => {
      if (row?.IndentID) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/popage/indentitems`,
            {
              params: { indentId: row.IndentID },
            }
          );

          const indentStatus =
            response.data.length > 0
              ? {
                  l1a: response.data[0].L1A || 0,
                  l1: response.data[0].L1 || 0,
                  l2: response.data[0].L2 || 0,
                  po: response.data[0].PO || 0,
                }
              : { l1a: 0, l1: 0, l2: 0, po: 0 };

          const draftItems = response.data.map((item) => ({
            itemID: item.ItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            total: item.Quantity * (item.Stock || 0),
            accountingUnitID: item.AccountingUnitID,
            unitname: item.UnitName,
          }));

          setItems(draftItems);
          setIsDraft(true);

          setRow((prev) => ({
            ...prev,
            L1A: indentStatus.l1a,
            L1: indentStatus.l1,
            L2: indentStatus.l2,
            PO: indentStatus.po,
          }));
        } catch (error) {
          logError("Fetch Draft Items", error);
          setError("Failed to load draft items");
        }
      }
    };

    fetchDraftItems();
  }, [row?.IndentID, logError]);

  // Handle item updates
  const handleItemUpdate = useCallback(
    (updateInfo) => {
      setChangeLog((prev) => {
        const updatedLog = { ...prev };

        switch (updateInfo.type) {
          case "add":
            updatedLog.addedItems.push(updateInfo.item);
            saveChangeLog({ type: "add", item: updateInfo.item });
            break;
          case "delete":
            updatedLog.deletedItems.push(updateInfo.item);
            saveChangeLog({ type: "delete", item: updateInfo.item });
            break;
          case "update":
            const original = items.find(
              (item) => item.itemID === updateInfo.item.itemID
            );
            updatedLog.updatedItems.push({
              original,
              updated: updateInfo.item,
            });
            saveChangeLog({
              type: "update",
              original,
              updated: updateInfo.item,
            });
            break;
          default:
            break;
        }
        return updatedLog;
      });
    },
    [items, saveChangeLog]
  );

  const handleSubmit = useCallback(async () => {
    if (items.length === 0) {
      setError("Please add items before submitting");
      return;
    }

    try {
      const submissionData = {
        indentId: row.IndentID,
        userId: currentUserId,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          stock: item.stock,
          remarks: item.remarks || "",
          categoryID: item.categoryID,
          promisedDate: item.promisedDate,
          accountingUnitID: item.accountingUnitID || null,
        })),
      };

      const response = await axios.post(
        `${API_BASE_URL}/popage/indents/submit`,
        submissionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data && response.data.message) {
        setIsDraft(false);
        setIsEditable(false);

        navigate("/POpage", {
          state: {
            message: response.data.message || "Indent submitted successfully",
          },
        });
      }
    } catch (error) {
      // More detailed error handling
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Submission failed. Please try again.";

      setError(errorMessage);

      // Log the full error for debugging
      console.error("Submission Error:", error.response?.data || error);
    }
  }, [row, currentUserId, items, navigate]);

  const handleSaveDraft = async () => {
    if (!row?.IndentID) {
      setError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setError("Please add items before saving draft");
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/popage/indents/${row.IndentID}/draft`, {
        isDraft: true,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          remarks: item.remarks,
          categoryID: item.categoryID,
          stock: item.stock,
          promisedDate: item.promisedDate,
        })),
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      navigate("/POpage", {
        state: {
          message: "Draft saved successfully",
        },
      });
    } catch (error) {
      logError("Save Draft", error);
      setError("Failed to save draft. Please try again.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!row) {
    return (
      <div>
        <Header />
        <div className="container mx-auto p-6 text-center">
          <p className="text-red-500">
            {error ||
              "No indent selected. Please go back and select an indent."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container mx-auto">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold underline">PD Items</h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <IndentDetails
          row={row}
          onUpdate={(updatedRow) => {
            setRow((prev) => ({
              ...prev,
              ...updatedRow,
            }));
          }}
        />
        <ItemsTable
          indentID={row.IndentID}
          indentType={row.TypeName}
          items={items}
          onAddItem={(newItem) => setItems((prev) => [...prev, newItem])}
          onDeleteItem={(index) =>
            setItems((prev) => prev.filter((_, i) => i !== index))
          }
          onUpdateItem={handleItemUpdate}
          calculateTotalAmount={() =>
            items.reduce((acc, item) => acc + (item.total || 0), 0).toFixed(2)
          }
          isDraft={isDraft}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onPrint={handlePrint}
          isEditable={isEditable}
          indentStatus={{
            l1a: row.L1A || 0,
            l1: row.L1 || 0,
            l2: row.L2 || 0,
            po: row.PO || 0,
          }}
        />

        <IndentChangesTracker
          indentID={row.IndentID.toString()}
          changes={{
            addedItems: [...changeLog.addedItems],
            deletedItems: [...changeLog.deletedItems],
            updatedItems: [
              ...changeLog.updatedItems,
              ...storedChangeLogs
                .filter((log) => log.change_type === "update")
                .map((log) => ({
                  original: log.original_data,
                  updated: log.updated_data,
                })),
            ],
          }}
        />
      </div>
    </div>
  );
};

export default POInvoicingPage;
